/**
 * AUTO SCROLL HOOK
 * Smooth scrolling to focused cells
 * @version 1.0.0
 * @production-ready
 */

import { useCallback, useRef } from 'react';

export interface UseAutoScrollReturn {
  scrollToCell: (rowIndex: number, colIndex: number) => void;
  registerInput: (el: HTMLInputElement | null, row: number, col: number) => void;
}

export const useAutoScroll = (
  containerRef: React.RefObject<HTMLDivElement | null> // ✅ Fixed: Accept nullable ref
): UseAutoScrollReturn => {
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  /**
   * Create ref key
   */
  const getRefKey = (row: number, col: number): string => {
    return `${row}:${col}`;
  };

  /**
   * Register input element
   */
  const registerInput = useCallback((
    el: HTMLInputElement | null,
    row: number,
    col: number
  ) => {
    inputRefs.current[getRefKey(row, col)] = el;
  }, []);

  /**
   * Scroll to specific cell with smooth animation
   */
  const scrollToCell = useCallback((rowIndex: number, colIndex: number) => {
    if (!containerRef.current) return; // ✅ Null check

    const container = containerRef.current;
    const cellKey = getRefKey(rowIndex, colIndex);
    const input = inputRefs.current[cellKey];

    if (!input) return;

    const containerRect = container.getBoundingClientRect();
    const cellRect = input.getBoundingClientRect();
    const scrollMargin = 20;

    let newScrollLeft = container.scrollLeft;
    let newScrollTop = container.scrollTop;

    // Calculate horizontal scroll
    if (cellRect.left < containerRect.left) {
      newScrollLeft = container.scrollLeft - (containerRect.left - cellRect.left) - scrollMargin;
    } else if (cellRect.right > containerRect.right) {
      newScrollLeft = container.scrollLeft + (cellRect.right - containerRect.right) + scrollMargin;
    }

    // Calculate vertical scroll
    if (cellRect.top < containerRect.top) {
      newScrollTop = container.scrollTop - (containerRect.top - cellRect.top) - scrollMargin;
    } else if (cellRect.bottom > containerRect.bottom) {
      newScrollTop = container.scrollTop + (cellRect.bottom - containerRect.bottom) + scrollMargin;
    }

    // Perform smooth scroll if needed
    if (newScrollLeft !== container.scrollLeft || newScrollTop !== container.scrollTop) {
      container.scrollTo({
        left: Math.max(0, newScrollLeft),
        top: Math.max(0, newScrollTop),
        behavior: 'smooth'
      });
    }

    // Focus and select input after scroll
    setTimeout(() => {
      if (input) {
        input.focus();
        // Only call select() if it's an actual input element (not a div from CustomSelect/CustomTimeInput)
        if (input instanceof HTMLInputElement && typeof input.select === 'function') {
          input.select();
        }
      }
    }, 150);
  }, [containerRef]);

  return {
    scrollToCell,
    registerInput
  };
};
